/**
 * This script is an api for app-server javascript handlers, which wraps and provides access to internal site-builder
 *
 * functions such as deep-linking into rendered areas (site-admin,etc), opening/closing/saving module views
 *
 * @author znsakala
 */
import $ = require('jquery');
import l10n = require('diy/util/l10n');
import appsApiConstants = require('./appsApiConstants');
let appsCatalog = require<any>('diy/site-admin/apps-catalog/appsCatalog');
import moduleSelector from 'diy/site-admin/module-selector/moduleSelector';
let siteAdminApi = require<any>('diy/site-admin/main/api');
import progressDialogApi = require('diy/progress-dialog/ProgressDialogApi');
import progressDialogDispatcher = require('diy/progress-dialog/ProgressDialogDispatcher');
import {TaskPayload} from 'diy/progress-dialog/ProgressDialogDispatcher';
import infoDialogApi = require('diy/info-dialog/InfoDialogApi');
import websiteData = require('diy/util/websiteData');

const mainCategoryRequestUrl = '/app/siteadmin/appscatalog/webapps-get-main-category';
let wbcExternalSettingLink: string = null;

interface Result {
    pageId: string;
    status: string;
}

export class AppsIntegration {
    /**
     * Opens app catalog site-admin fly-out window
     */
    openAppCatalog() {
        appsCatalog.customOpen();
    };

    /**
     * Opens semantic data dialog in site-admin bar
     */
    openSemanticDataDialog() {
        diy.editor.deeplinking.requestPath([
            {label: 'siteadmin', params: 'settings'},
            {label: 'SAC.Settings', params: 'personal-data'}
        ]);
    }

    /**
     * Opens module selector fly-out window and highlights a specific module selector item identified by webComponentId
     *
     * @param webComponentId id of web component item to be highlighted on module selector
     *
     * @throws error if given web component id is invalid or corresponding main category on module selector is not found
     */
    openModuleSelector(webComponentId: string) {
        $.ajax({
            url: mainCategoryRequestUrl,
            type: 'POST',
            dataType: 'json',
            data: {wc_id: webComponentId},
            success: function (result) {
                if (!result.data.main_category) {
                    throw appsApiConstants.errors.MODULE_MAIN_CATEGORY_NOT_FOUND + ': ' + webComponentId;
                }
                moduleSelector.jumpToWebComponent(webComponentId, result.data.main_category);
            },
            error: function () {
                throw appsApiConstants.errors.MODULE_SELECTOR_OPENING_EVENT + ': ' + webComponentId;
            }
        });
    };

    /**
     * Adds a page to site-builder containing a webComponent identified by given web-component-id
     *
     * @param webComponentId  parameter determining which webcomponent hast to be added to new pages
     * @param externalSettingsLink link to external settings for concerned webcomponent
     * @param isUniqueRequest parameter determining whether this request should occur only once or not
     *
     * @throws error if page creation job could not be scheduled
     */
    addPageWithWebComponent(
        webComponentId: string,
        externalSettingsLink: string,
        isUniqueRequest: boolean
    ) {

        siteAdminApi.close();

        $.ajax({
            url: '/app/cms/webcomponentpage/add',
            type: 'POST',
            dataType: 'json',
            headers: {
                'Accept': 'application/vnd.oneandone.diy.website.webcomponent-page-creation+json'
            },
            data: {
                isUniqueRequest: isUniqueRequest,
                webComponentId: webComponentId
            },
            success: (result: Result) => {
                if (result.status !== 'job_skipped') {
                    this.initPageCreationProgressDialog(webComponentId);
                    this.runPageCreationProgressTasks(webComponentId, isUniqueRequest, result);
                    wbcExternalSettingLink = externalSettingsLink;
                }
            },
            error: function () {
                throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
            }
        });
    };

    /**
     * Triggers display of page creation progress dialog
     */
    initPageCreationProgressDialog(webComponentId: string) {
        progressDialogApi.showDialog(
            [
                {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PREPARATION,
                    title: l10n.getString(
                        'webComponentPageCreation.progress.successfull.steps.preparation.' +
                        webComponentId
                    )
                },
                {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PAGE_CREATION,
                    title: l10n.getString(
                        'webComponentPageCreation.progress.successfull.steps.pageCreation.' +
                        webComponentId
                    )
                },
                {
                    identifier: appsApiConstants.pageCreationSteps.STEP_WEBCOMPONENT_CREATION,
                    title: l10n.getString(
                        'webComponentPageCreation.progress.successfull.steps.webcomponentCreation.' +
                        webComponentId
                    )
                }
            ],
            {
                title: l10n.getString('webComponentPageCreation.progress.successfull.title.' + webComponentId),
                description: l10n.getString(
                    'webComponentPageCreation.progress.successfull.description.' + webComponentId
                ),
                image: websiteData.getCdnUrl() + 's/diy/site-admin/main/img/webcomponent-page-progress.png',
                imageHeight: 220
            }
        );
    };

    /**
     * Polls page creation tasks status and update dialog content accordingly
     *
     * @param webComponentId  web component id to be used to
     * @param isUniqueRequest flag determining whether process can be triggered several times or not
     * @param creationFeature page create feature resource provided by backend
     */
    runPageCreationProgressTasks(
        webComponentId: string,
        isUniqueRequest: boolean,
        creationFeature: Result
    ) {

        if (creationFeature.status === 'job_aborted') {
            progressDialogDispatcher.dispatch(
                this.getProgressTaskStatus(creationFeature.status)
            );
            throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
        }

        if (creationFeature.status === 'job_finished') {
            if (!creationFeature.pageId) {
                progressDialogDispatcher.dispatch(
                    this.getProgressTaskStatus('job_aborted')
                );
                throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
            } else {
                this.showPageCreationSuccessDialog(webComponentId, creationFeature.pageId);
            }

            return;
        }

        progressDialogDispatcher.dispatch(
            this.getProgressTaskStatus(creationFeature.status)
        );

        setTimeout(() => {
            $.ajax({
                url: '/app/cms/webcomponentpage/status',
                type: 'GET',
                dataType: 'json',
                headers: {
                    'Accept': 'application/vnd.oneandone.diy.website.webcomponent-page-creation+json'
                },
                data: {
                    isUniqueRequest: isUniqueRequest,
                    webComponentId: webComponentId
                },
                success: (result: Result) => {
                    if (!result.status) {
                        throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
                    } else {
                        this.runPageCreationProgressTasks(webComponentId, isUniqueRequest, result);
                    }
                },
                error: function () {
                    throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
                }
            });
        }, 2000);
    };

    /**
     * Triggers display of web-component success dialog
     */
    showPageCreationSuccessDialog(webComponentId: string, pageId: string) {
        progressDialogApi.hideDialog();

        setTimeout(() => {
            infoDialogApi.showDialog(
                {
                    title: l10n.getString('webComponentPageCreation.success.title.' + webComponentId),
                    description: this.getPageCreationDialogSuccessDescription(webComponentId),
                    image: websiteData.getCdnUrl() + 's/diy/site-admin/main/img/webcomponent-page-success.png',
                    imageHeight: 220,

                    primaryButtonConfig: {
                        label: l10n.getString('webComponentPageCreation.success.invitation.button.' + webComponentId),
                        callback: () => {
                            $(document).trigger(
                                "diy-editor-generic-linkInteraction",
                                {
                                    pageName: "MyShopReady",
                                    actionName: "jumptoPage"
                                }
                            );
                            this.disablePageCreationSuccessDialog(webComponentId, 'notification_accepted', function () {
                                location.href = cc_.properties.cmsUrl + 'app/' + script_basisID + '/' + pageId;
                            });
                        }
                    },

                    secondaryButtonConfig: {
                        label: l10n.getString('webComponentPageCreation.success.closeButton'),
                        callback: () => {
                            $(document).trigger(
                                "diy-editor-generic-linkInteraction",
                                {
                                    pageName: "MyShopReady",
                                    actionName: "close"
                                }
                            );
                            this.disablePageCreationSuccessDialog(webComponentId, 'notification_rejected', function () {
                                location.reload();
                            });
                        }
                    }
                }
            );
        }, 300);

        $(document).trigger('diy-editor-generic-sectionAccessed',
            {'sectionCodeName': 'myShopReady'});
    };

    /**
     * triggers deactivation of success dialog per website feature deactivation on server
     *
     * @param webComponentId concerned webComponentId
     * @param status disabling status
     * @param callback
     */
    disablePageCreationSuccessDialog(
        webComponentId: string,
        status: string,
        callback: () => any
    ) {
        $.ajax({
            url: '/app/cms/webcomponentpage/disable-notification',
            type: 'POST',
            dataType: 'json',
            headers: {
                'Accept': 'application/vnd.oneandone.diy.website.webcomponent-page-notification+json'
            },
            data: {
                webComponentId: webComponentId,
                status: status
            },
            error: function () {
                throw appsApiConstants.errors.INTERNAL_SERVER_ERROR;
            },
            success: function() {
                if (typeof callback === 'function') {
                    callback();
                }
            }
        });
    };

    /**
     * Retrieves description of success dialog message to be displayed
     *
     * Note that if appserver API caller provided an externalSettingsLink,
     * this one will be appended to description too
     */
    getPageCreationDialogSuccessDescription(webComponentId: string) {
        let link = '';
        let description = l10n.getString('webComponentPageCreation.success.description.' + webComponentId);
        let trackJumpLink = function() {
            $(document).trigger(
                'diy-editor-generic-linkInteraction',
                {
                    'pageName': "MyShopReady",
                    'actionName': 'jumptoMBO'
                }
            );
        };

        $('body').on('click', '[data-track="myshop-flyout-jumpout"]', trackJumpLink);

        if (wbcExternalSettingLink) {
            link = $('<a href="' + wbcExternalSettingLink + '" target="_blank" data-track="myshop-flyout-jumpout"' +
                'style="text-decoration:none; color:#0FABFC;">' + ' ' +
                l10n.getString('webComponentPageCreation.success.invitation.link.'
                + webComponentId) + '</a>')[0].outerHTML;
        } else {
            link = l10n.getString('webComponentPageCreation.success.invitation.link.' + webComponentId);
        }

        return description.replace('{link}', link);
    };

    /**
     * Retrieves current page creation progress status
     */
    getProgressTaskStatus(jobStatus: string): TaskPayload {
        let taskStatus = {} as TaskPayload;

        switch (jobStatus) {
            case 'job_empty':
                taskStatus = {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PREPARATION,
                    state: 1
                };
                break;

            case 'job_started':
                taskStatus = {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PREPARATION,
                    state: 2
                };
                break;

            case 'page_created':
                taskStatus = {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PAGE_CREATION,
                    state: 2
                };
                break;

            case 'web_component_added':
                taskStatus = {
                    identifier: appsApiConstants.pageCreationSteps.STEP_WEBCOMPONENT_CREATION,
                    state: 2
                };
                break;

            case 'job_aborted':
                taskStatus = {
                    identifier: appsApiConstants.pageCreationSteps.STEP_PREPARATION,
                    state: 3
                };
                break;

            default:
                break;
        }

        return taskStatus;
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/external/AppsIntegration.ts
 **/